import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { clearUserSession } from "../auth/session";
import { apiCall } from "../utils/apiService";
import {
  readCachedInboxUnread,
  writeCachedInboxUnread,
} from "../utils/inboxUnread";
import { getWsBaseUrl } from "../utils/wsUrl";
import { bindInboxSoundUnlock, playInboxChime } from "../utils/inboxSound";
import { getPlanFeatures } from "../utils/planFeatures";
import "./MasterLayout.css";

const getUserInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.[0] || "U").toUpperCase();
};

const resolveUserProfileSrc = (image) => {
  if (!image || image === "undefined" || image === "null") return null;
  const base = process.env.REACT_APP_FILE_URL;
  if (!base) return null;
  return `${base}/profile/${image}`;
};

const formatNotifyTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

const notifyIcon = (type) => {
  if (type === "session_disconnect") return "mdi:whatsapp";
  if (type === "campaign_alert") return "mdi:bullhorn-outline";
  return "mdi:bell-outline";
};

const MasterLayout = ({ children, hideFooter = false }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  let [currentYear, setCurrentYear] = useState([]);
  let [user, setUser] = useState(null);
  const [profileImgFailed, setProfileImgFailed] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(() => readCachedInboxUnread());
  const [notifications, setNotifications] = useState([]);
  const [notifyUnread, setNotifyUnread] = useState(0);
  const location = useLocation(); // Hook to get the current route
  const isChatPage = hideFooter || location.pathname === "/chat-messages";
  const profileSrc = resolveUserProfileSrc(user?.image);
  const chatEnabled =
    user == null
      ? true
      : user.chat_enabled !== 0 && user.chat_enabled !== false;
  const isFreeAccount = Number(user?.is_free) === 1;
  const planFeatures = getPlanFeatures(user);
  // Until user profile loads, keep menu visible to avoid flicker; after load, gate by plan.
  const featuresReady = user != null;
  const showApi = !featuresReady || planFeatures.api;
  const showChatbot = !featuresReady || planFeatures.chatbot;
  const showCampaigns = !featuresReady || planFeatures.bulk;
  const showWebhook = !featuresReady || planFeatures.webhook;
  const showDevelopers = showApi || showWebhook;

  const applyInboxUnread = useCallback((count) => {
    const n = writeCachedInboxUnread(count);
    setInboxUnread(n);
    return n;
  }, []);

  const refreshInboxUnread = useCallback(async () => {
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (stored?.chat_enabled === 0 || stored?.chat_enabled === false) {
        applyInboxUnread(0);
        return;
      }
    } catch {
      /* continue — still try to fetch */
    }

    try {
      const res = await apiCall({ url: "social/unread-count", method: "GET" });
      if (res?.success) applyInboxUnread(Number(res.unreadCount) || 0);
    } catch {
      /* keep cached count; later updates arrive on the WebSocket */
    }
  }, [applyInboxUnread]);

  const logout = () => {
    clearUserSession();
    setTimeout(() => {
      window.location.href = "/login";
    }, 500);
  };

  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      stored = null;
    }
    setUser(stored);
    setProfileImgFailed(false);

    (async () => {
      try {
        const res = await apiCall({ url: "user/get-user-by-token", method: "GET" });
        if (res?.success && res.data) {
          setUser(res.data);
          try {
            localStorage.setItem("user", JSON.stringify(res.data));
            let planInstance = 0;
            try {
              const plan =
                typeof res.data.plan === "string"
                  ? JSON.parse(res.data.plan)
                  : res.data.plan;
              planInstance = Number(plan?.instance) || 0;
            } catch {
              planInstance = 0;
            }
            const limit = Math.max(
              0,
              Number(res.data.allow_multi_instance) || planInstance || 0
            );
            localStorage.setItem("instanceLimit", String(limit));
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* keep cached user */
      }
    })();
    const currentYear = new Date().getFullYear(); // Get the year as a number
    setCurrentYear(currentYear);
    // Function to handle dropdown clicks
    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest('.dropdown');

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains('open');

      // Close all dropdowns
      const allDropdowns = document.querySelectorAll('.sidebar-menu .dropdown');
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove('open');
      });

      // Toggle the clicked dropdown
      if (!isActive) {
        clickedDropdown.classList.add('open');
      }
    };

    // Attach click event listeners to all dropdown triggers
    const dropdownTriggers = document.querySelectorAll('.sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link');

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener('click', handleDropdownClick);
    });

    // Function to open submenu based on current route
    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll('.sidebar-menu .dropdown');
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll('.sidebar-submenu li a');
        submenuLinks.forEach((link) => {
          if (link.getAttribute('href') === location.pathname || link.getAttribute('to') === location.pathname) {
            dropdown.classList.add('open');
          }
        });
      });
    };

    // Open the submenu that contains the open route
    openActiveDropdown();


    // Cleanup event listeners on unmount
    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener('click', handleDropdownClick);
      });

    };
  }, [location.pathname]);

  // Sidebar inbox badge: one load, then live WebSocket updates (no polling).
  useEffect(() => {
    if (!chatEnabled) {
      applyInboxUnread(0);
      return undefined;
    }

    setInboxUnread(readCachedInboxUnread());
    refreshInboxUnread();

    const onCustom = (e) => {
      const n = Number(e?.detail?.unreadCount);
      if (Number.isFinite(n)) applyInboxUnread(n);
    };
    window.addEventListener("inbox-unread-changed", onCustom);

    let socket;
    let pingInterval;
    let reconnectTimer;
    let closed = false;

    const connect = () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      socket = new WebSocket(getWsBaseUrl());
      socket.onopen = () => {
        socket.send(JSON.stringify({ type: "auth", token }));
        pingInterval = setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }));
          }
        }, 25000);
      };
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data?.type === "unread_count" && Number.isFinite(Number(data.unreadCount))) {
            applyInboxUnread(Number(data.unreadCount) || 0);
          }
        } catch {
          /* ignore */
        }
      };
      socket.onclose = () => {
        clearInterval(pingInterval);
        if (!closed) reconnectTimer = setTimeout(connect, 5000);
      };
    };

    connect();

    return () => {
      closed = true;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer);
      window.removeEventListener("inbox-unread-changed", onCustom);
      if (socket && socket.readyState === WebSocket.OPEN) socket.close();
    };
  }, [chatEnabled, refreshInboxUnread, applyInboxUnread]);

  const refreshNotifications = useCallback(async () => {
    try {
      const res = await apiCall({ url: "features/notifications", method: "GET" });
      if (res?.success) {
        setNotifications(Array.isArray(res.data) ? res.data : []);
        setNotifyUnread(Number(res.unreadCount) || 0);
      }
    } catch {
      /* keep last list */
    }
  }, []);

  const markOneRead = async (item) => {
    if (!item?.id) return;
    if (!Number(item.is_read)) {
      setNotifications((list) =>
        list.map((n) => (n.id === item.id ? { ...n, is_read: 1 } : n))
      );
      setNotifyUnread((c) => Math.max(0, c - 1));
      try {
        const res = await apiCall({
          url: "features/notifications/read",
          method: "POST",
          body: { id: item.id },
        });
        if (res?.success && res.unreadCount !== undefined) {
          setNotifyUnread(Number(res.unreadCount) || 0);
        }
      } catch {
        /* optimistic UI kept */
      }
    }
  };

  const markAllRead = async () => {
    setNotifications((list) => list.map((n) => ({ ...n, is_read: 1 })));
    setNotifyUnread(0);
    try {
      await apiCall({ url: "features/notifications/read-all", method: "POST", body: {} });
    } catch {
      refreshNotifications();
    }
  };

  useEffect(() => {
    refreshNotifications();
    const poll = setInterval(refreshNotifications, 45000);
    return () => clearInterval(poll);
  }, [refreshNotifications, location.pathname]);

  // Unlock audio after first click so inbox chimes can play later (browser policy).
  useEffect(() => bindInboxSoundUnlock(), []);

  // Live header alerts + inbox sound via WebSocket (works on every page)
  useEffect(() => {
    const wsBase = getWsBaseUrl();
    let socket;
    let pingInterval;
    let reconnectTimer;
    let unmounted = false;

    const connect = () => {
      socket = new WebSocket(wsBase);
      socket.onopen = () => {
        const token = localStorage.getItem("token");
        if (token) socket.send(JSON.stringify({ type: "auth", token }));
        pingInterval = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
      };
      socket.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (data.type === "user_notification" && data.notification) {
          setNotifications((list) => {
            if (list.some((n) => String(n.id) === String(data.notification.id))) return list;
            return [data.notification, ...list].slice(0, 40);
          });
          setNotifyUnread((c) => c + 1);
        }

        const isChatPayload =
          data.type === "chat_message" ||
          (data.text != null && (data.chat_id != null || data.sid));
        if (isChatPayload && data.sender === "other") {
          playInboxChime();
        }
      };
      socket.onclose = () => {
        clearInterval(pingInterval);
        if (!unmounted) reconnectTimer = setTimeout(connect, 8000);
      };
      socket.onerror = () => {
        try {
          socket.close();
        } catch {
          /* ignore */
        }
      };
    };

    connect();
    return () => {
      unmounted = true;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer);
      try {
        socket?.close();
      } catch {
        /* ignore */
      }
    };
  }, []);


  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const post = { padding: '8px', fontSize: '9px', background: '#4CAF50' }

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      {/* sidebar */}
      <aside className={sidebarActive ? "sidebar active " : mobileMenu ? "sidebar sidebar-open" : "sidebar"}>
        <button onClick={mobileMenuControl} type="button" className="sidebar-close-btn">
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link to="/dashboard" className="sidebar-logo">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/alphaxcloud.svg`}
              alt="AlphaXCloud"
              className="light-logo"
              width={180}
            />
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/alphaxcloud.svg`}
              alt="AlphaXCloud"
              className="dark-logo"
              width={180}
            />
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logo-icon.svg`}
              alt="AlphaXCloud"
              className="logo-icon"
              width={36}
              height={36}
            />
          </Link>
        </div>
        <div className="sidebar-menu-area">
          <ul className="sidebar-menu" id="sidebar-menu">
            <li>
              <NavLink to="/dashboard" className={(navData) =>
                navData.isActive ? "active-page" : ""
              }>
                <Icon icon="solar:home-smile-angle-outline" className="menu-icon" />
                <span>Dashboard</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/instances" className={(navData) =>
                navData.isActive ? "active-page" : ""
              }>
                <Icon icon="mdi:whatsapp" className="menu-icon" />
                <span>Sessions</span>
              </NavLink>
            </li>
            {showApi ? (
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="mdi:flask-outline" className="menu-icon" />
                  <span>API Tester</span>
                </Link>
                <ul className="sidebar-submenu ps-0">
                  <li>
                    <NavLink to="/chat-single" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      <div className="badge" style={post}>POST</div>Text
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/chat-image" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      <div className="badge" style={post}>POST</div>Image
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/chat-audio" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      <div className="badge" style={post}>POST</div>Audio
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/chat-video" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      <div className="badge" style={post}>POST</div>Video
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/chat-contact" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      <div className="badge" style={post}>POST</div>Contact
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/chat-location" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      <div className="badge" style={post}>POST</div>Location
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/chat-group" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      <div className="badge" style={post}>POST</div>Group
                    </NavLink>
                  </li>
                </ul>
              </li>
            ) : null}
            {chatEnabled && (
              <li>
                <NavLink to="/chat-messages" className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }>
                  <Icon icon="mdi:message-text-outline" className="menu-icon" />
                  <span>Inbox</span>
                  {inboxUnread > 0 && (
                    <em className="inbox-unread-badge" title={`${inboxUnread} unread`}>
                      {inboxUnread > 99 ? "99+" : inboxUnread}
                    </em>
                  )}
                </NavLink>
              </li>
            )}
            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:message-cog-outline" className="menu-icon" />
                <span>Messaging</span>
              </Link>
              <ul className="sidebar-submenu">
                {showChatbot ? (
                  <li>
                    <NavLink to="/automation" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      Chatbot
                    </NavLink>
                  </li>
                ) : null}
                <li>
                  <NavLink to="/quick-replies" className={(navData) =>
                    navData.isActive ? "active-page" : ""
                  }>
                    Quick Replies
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/templates" className={(navData) =>
                    navData.isActive ? "active-page" : ""
                  }>
                    Templates
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/media-library" className={(navData) =>
                    navData.isActive ? "active-page" : ""
                  }>
                    Media Library
                  </NavLink>
                </li>
              </ul>
            </li>
            <li>
              <NavLink to="/logs" className={(navData) =>
                navData.isActive ? "active-page" : ""
              }>
                <Icon icon="mdi:clipboard-text-clock-outline" className="menu-icon" />
                <span>Message Logs</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/phonebooks" className={(navData) =>
                navData.isActive ? "active-page" : ""
              }>
                <Icon icon="mdi:account-box-outline" className="menu-icon" />
                <span>Contacts</span>
              </NavLink>
            </li>
            {showCampaigns ? (
              <li>
                <NavLink to="/campaigns" className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }>
                  <Icon icon="mdi:bullhorn-outline" className="menu-icon" />
                  <span>Campaigns</span>
                </NavLink>
              </li>
            ) : null}
            {!isFreeAccount ? (
              <li>
                <NavLink to="/plans" className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }>
                  <Icon icon="mdi:package-variant-closed" className="menu-icon" />
                  <span>Plans & Orders</span>
                </NavLink>
              </li>
            ) : null}
            {showDevelopers ? (
            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:code-braces" className="menu-icon" />
                <span>Developers</span>
              </Link>
              <ul className="sidebar-submenu">
                {showApi ? (
                  <li>
                    <NavLink to="/api-access" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      API Keys
                    </NavLink>
                  </li>
                ) : null}
                {showWebhook ? (
                  <li>
                    <NavLink to="/webhook-settings" className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }>
                      Webhooks
                    </NavLink>
                  </li>
                ) : null}
              </ul>
            </li>
            ) : null}
            <li>
              <NavLink to="/settings" className={(navData) =>
                navData.isActive ? "active-page" : ""
              }>
                <Icon icon="solar:settings-outline" className="menu-icon" />
                <span>Settings</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/support" className={(navData) =>
                navData.isActive ? "active-page" : ""
              }>
                <Icon icon="mdi:lifebuoy" className="menu-icon" />
                <span>Support</span>
              </NavLink>
            </li>
          </ul>
        </div>
      </aside>

      <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button type="button" className="sidebar-toggle" onClick={sidebarControl}>
                  {
                    sidebarActive ? (<Icon
                      icon="iconoir:arrow-right"
                      className="icon text-2xl non-active"
                    />) : (<Icon
                      icon="heroicons:bars-3-solid"
                      className="icon text-2xl non-active "
                    />)
                  }
                </button>
                <button onClick={mobileMenuControl} type="button" className="sidebar-mobile-toggle">
                  <Icon
                    icon="heroicons:bars-3-solid"
                    className="icon"
                  />
                </button>
                <Link to="/" className="nav-home-btn" aria-label="Home">
                  <Icon icon="mdi:web" className="icon text-xl" />
                  <span className="nav-home-tip">Home</span>
                </Link>
                <form className="navbar-search">
                  <input type="text" name="search" placeholder="Search" />
                  <Icon icon="ion:search-outline" className="icon" />
                </form>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                {/* ThemeToggleButton */}
                <ThemeToggleButton />
                {/* Message dropdown end */}
                <div className="dropdown header-notify-dropdown">
                  <button
                    className="header-notify-btn has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center position-relative"
                    type="button"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    aria-label="Notifications"
                    onClick={() => refreshNotifications()}
                  >
                    <Icon
                      icon="iconoir:bell"
                      className="text-success-light text-xl"
                    />
                    {notifyUnread > 0 && (
                      <span className="header-notify-dot">
                        {notifyUnread > 99 ? "99+" : notifyUnread}
                      </span>
                    )}
                  </button>
                  <div className="dropdown-menu dropdown-menu-end p-0 header-notify-menu">
                    <div className="header-notify-head">
                      <div>
                        <h6>Notifications</h6>
                        <p>Session &amp; campaign alerts</p>
                      </div>
                      {notifyUnread > 0 ? (
                        <span className="header-notify-count">{notifyUnread > 99 ? "99+" : notifyUnread}</span>
                      ) : null}
                    </div>

                    <div className="header-notify-body">
                      {notifications.length === 0 ? (
                        <div className="header-notify-empty">
                          <span className="header-notify-empty-icon">
                            <Icon icon="mdi:bell-outline" width={22} />
                          </span>
                          <strong>No alerts yet</strong>
                          <span>
                            Turn on header notifications in Settings to see session and campaign alerts here.
                          </span>
                          <Link to="/settings" className="header-notify-empty-link">
                            Open alert settings
                          </Link>
                        </div>
                      ) : (
                        notifications.map((item) => {
                          const unread = !Number(item.is_read);
                          const dest = item.link || "/settings";
                          return (
                            <Link
                              key={item.id}
                              to={dest}
                              onClick={() => markOneRead(item)}
                              className={`header-notify-item${unread ? " is-unread" : ""}`}
                            >
                              <span className={`header-notify-item-icon type-${item.type || "info"}`}>
                                <Icon icon={notifyIcon(item.type)} width={18} />
                              </span>
                              <span className="header-notify-item-copy">
                                <span className="header-notify-item-title">
                                  {item.title}
                                  {unread ? <em>New</em> : null}
                                </span>
                                <span className="header-notify-item-body">
                                  {item.body || "Open for details"}
                                </span>
                              </span>
                              <span className="header-notify-item-time">
                                {formatNotifyTime(item.createdAt)}
                              </span>
                            </Link>
                          );
                        })
                      )}
                    </div>

                    <div className="header-notify-foot">
                      {notifyUnread > 0 ? (
                        <button type="button" className="header-notify-foot-btn" onClick={markAllRead}>
                          Mark all read
                        </button>
                      ) : null}
                      <Link to="/settings" className="header-notify-foot-link">
                        Alert settings
                      </Link>
                    </div>
                  </div>
                </div>
                {/* Notification dropdown end */}
                <div className="dropdown">
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-label="User menu"
                  >
                    {profileSrc && !profileImgFailed ? (
                      <img
                        src={profileSrc}
                        alt={user?.name || "User"}
                        className="w-40-px h-40-px object-fit-cover rounded-circle"
                        onError={() => setProfileImgFailed(true)}
                      />
                    ) : (
                      <span
                        className="w-40-px h-40-px rounded-circle d-flex justify-content-center align-items-center fw-semibold text-sm bg-success-100 text-success-600"
                        aria-hidden="true"
                      >
                        {getUserInitials(user?.name)}
                      </span>
                    )}
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm">
                    <div className="py-12 px-16 radius-8 bg-success-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <h6 className="text-lg text-success-light fw-semibold mb-2">
                          {user?.name}
                        </h6>
                        <span className="text-secondary-light fw-medium text-sm">User</span>
                      </div>
                      <button type="button" className="hover-text-danger">
                        <Icon icon="radix-icons:cross-1" className="icon text-xl" />
                      </button>
                    </div>
                    <ul className="to-top-list">
                      <li>
                        <Link
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-success d-flex align-items-center gap-3"
                          to="/my-profile"
                        >
                          <Icon icon="solar:user-linear" className="icon text-xl" />
                          My Profile
                        </Link>
                      </li>
                      <li>
                        <Link
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-success d-flex align-items-center gap-3"
                          to="/settings"
                        >
                          <Icon icon="solar:settings-outline" className="icon text-xl" />
                          Settings
                        </Link>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3 w-100 border-0 bg-transparent"
                          onClick={logout}
                        >
                          <Icon icon="lucide:power" className="icon text-xl" /> Log Out
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}

              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className={`dashboard-main-body${isChatPage ? " chat-page-body" : ""}`}>
          {children}
        </div>

        {/* Footer section */}
        {!isChatPage && (
          <footer className="d-footer">
            <div className="row align-items-center justify-content-between">
              <div className="col-auto">
                <p className="mb-0">© {currentYear} AlphaXCloud. All Rights Reserved.</p>
              </div>
              <div className="col-auto">
                <p className="mb-0">
                  Made by <span className="text-success-600">RCA</span>
                </p>
              </div>
            </div>
          </footer>
        )}
      </main>
    </section>
  );
};

export default MasterLayout;
